import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PrintDialogComponent, PrintPageType, PrintType } from '@components/print-dialog/print-dialog.component';
import { CanvasService } from '@service/canvas/canvas.service';
import { EditorService } from '@service/editor.service';
import { PrintMimicsService } from '@service/print-mimics.service';
import { Mimic } from 'icreate/common/models/mimic';
import { lastValueFrom } from 'rxjs';
import * as _ from 'lodash';
import { PullProjectService } from '@service/pull-project.service';
import { Vessel } from 'icreate/common/models/vessel';
import { Library, LibraryAndCategories } from 'icreate/common/models/library';
import { LibraryCategory } from 'icreate/common/models/library-category';
import { LibraryComponentService } from '@service/library-component.service';
import { LibraryComponent } from 'icreate/common/models/library-component';
import { LibraryCategoryService } from '@service/library-category.service';
import { LibraryCategoryDialogComponent } from '@components/editor/library/library-category-dialog/library-category-dialog.component';
import { MimicFolderViewData, RootItem, RootItemType } from 'icreate/common/models/mimic-folder';
import { EditorMimicActionsService } from '@service/editor-mimic-actions.service';
import { ButtonLabel, DuplicateItemType } from '@helpers/constants';
import { DuplicateMimicDialogComponent } from '@components/editor/duplicate-mimic-dialog/duplicate-mimic-dialog.component';
import { Utils } from '@helpers/utils';

enum TAB {
    mimic = 'Mimics',
    component = 'Components',
}

@Component({
    selector: 'app-editor-navigator',
    templateUrl: './editor-navigator.component.html',
    styleUrls: ['./editor-navigator.component.css'],
    standalone: false,
})
export class EditorNavigatorComponent {
    readonly COMPONENT_DAILOG_TOP = 48;
    readonly COMPONENT_DAILOG_LEFT = 256;
    readonly CLICK_EVENT = 'click';
    readonly TABS = TAB;

    @Input() vessel: Vessel;
    @Input() rootList: RootItem[] = [];
    @Input() libraryList: (Library & LibraryAndCategories)[] = [];
    @Input() canCreateOrDelete: boolean;
    @Output() onSelectMimic = new EventEmitter<Mimic>();
    @Output() onCreateMimic = new EventEmitter<{ mimicFolderId: string; index: number }>();
    @Output() onDeleteMimic = new EventEmitter<{
        mimic: Mimic;
    }>();
    @Output() onDuplicateMimic = new EventEmitter<Mimic>();
    @Output() onDeleteLibrary = new EventEmitter<Library>();
    @Output() onDeleteCategory = new EventEmitter<LibraryCategory>();
    @Output() onDeleteComponent = new EventEmitter<LibraryComponent>();
    @Output() onDeleteMimicFolder = new EventEmitter<MimicFolderViewData>();
    @Output() onOpenFolderExpanded = new EventEmitter<string>();
    @Output() onOpenLibraryExpanded = new EventEmitter<string>();
    @Output() onOpenComponentDialog = new EventEmitter<void>();

    @ViewChild('libraryComponentDialog') libraryComponentDialog: ElementRef;
    @ViewChild('categoryOptions') categoryOptions: ElementRef;
    @ViewChild('images', { static: true }) images: ElementRef;

    selectedMimic: Mimic = null;
    selectedCategoryComponentList: LibraryComponent[] = [];

    showOptionsAll: boolean = false;

    showOption: Mimic | Library = null;
    showCategoryOption: boolean = false;
    selectedCategory: LibraryCategory = null;

    showComponents: boolean = false;
    showComponentDialog: boolean = false;

    selectedTab: TAB = this.TABS.mimic;

    constructor(
        private editorService: EditorService,
        private printMimicsService: PrintMimicsService,
        private dialog: MatDialog,
        private canvasService: CanvasService,
        private pullService: PullProjectService,
        private libraryComponentService: LibraryComponentService,
        private libraryCategoryService: LibraryCategoryService,
        private editorMimicActionsService: EditorMimicActionsService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (this.showComponentDialog && !this.libraryComponentDialog?.nativeElement.contains(event.target)) {
            this.showComponentDialog = false;
        }

        if (this.showCategoryOption) {
            this.showCategoryOption = false;
        }
    }

    @HostListener('document:contextmenu', ['$event'])
    closeContext(event: any) {
        if (this.showCategoryOption) {
            this.showCategoryOption = false;
        }
    }

    createMimic(event: { mimicFolderId: string; index: number }) {
        this.onCreateMimic.emit(event);
    }

    selectMimic(mimic: Mimic) {
        this.onSelectMimic.emit(mimic);
    }

    openCategoryOptions(e: { category: LibraryCategory; event: MouseEvent }) {
        const { category, event } = e;
        event.preventDefault();
        event.stopPropagation();
        this.showOption = undefined;
        this.showOptionsAll = false;
        this.selectedCategory = category;
        this.showCategoryOption = true;
        this.showComponentDialog = false;

        const offsetX = event.clientX + 90;
        const offsetY = event.clientY;

        this.categoryOptions.nativeElement.style.top = `${offsetY}px`;
        this.categoryOptions.nativeElement.style.left = `${offsetX}px`;
    }

    async deleteMimic(mimic: Mimic) {
        this.onDeleteMimic.emit({
            mimic,
        });
    }

    async duplicateMimic(mimic: Mimic) {
        this.onDuplicateMimic.emit(mimic);
    }

    async printAll() {
        this.showOptionsAll = false;
        const allMimics = this.getAllMimics();
        const dialogRef = this.dialog.open(PrintDialogComponent, {
            data: {
                pageType: PrintPageType.ALL_MIMICS,
                vessel: this.vessel,
                iAPIVesselId: this.vessel.iAPIVesselId,
                shipyardId: this.vessel.shipyardId,
                options: allMimics,
                notMain: true,
            },
        });

        const shouldPrint = await lastValueFrom(dialogRef.afterClosed());
        if (!shouldPrint || shouldPrint.cancel) return;
        this.editorService.showLoadingSpinner(true);
        const { printType, version, iAPIShipyardlId, setListIds } = shouldPrint;

        if (printType) {
            if (printType === PrintType.NO_UPDATES) {
                allMimics
                    .filter(mimic => setListIds.has(mimic.id))
                    .forEach(mimic => {
                        if (mimic.id === this.selectedMimic?.id) {
                            const mimicToPrint = _.cloneDeep(mimic);
                            mimicToPrint.svgcontent = this.getSvgToPrint();
                            this.printMimicsService.addMimicToPrint(mimicToPrint, this.images);
                        } else {
                            this.printMimicsService.addMimicToPrint(mimic, this.images);
                        }
                    });
            } else if (printType === PrintType.UPDATES) {
                if (!iAPIShipyardlId || !version) return;
                const oldMimics = await this.pullMimics(iAPIShipyardlId, version);

                allMimics
                    .filter(mimic => setListIds.has(mimic.id))
                    .forEach(mimic => {
                        const mimicToPrint = _.cloneDeep(mimic);

                        if (mimicToPrint.id === this.selectedMimic?.id) {
                            mimicToPrint.svgcontent = this.getSvgToPrint();
                        }

                        const oldMimic = oldMimics.find(m => m.id === mimicToPrint.id);
                        const updatedSvg = this.printMimicsService.compareMimic(mimicToPrint, oldMimic);
                        mimicToPrint.svgcontent = updatedSvg;

                        this.printMimicsService.addMimicToPrint(mimicToPrint, this.images);
                    });
            }

            this.editorService.showLoadingSpinner(false);
            this.printMimicsService.printMimicsAsPdf(this.images, allMimics);
        }
    }

    async printMimic(mimic: Mimic) {
        const mimicName = mimic.name;

        this.showOption = null;
        const dialogRef = this.dialog.open(PrintDialogComponent, {
            data: {
                pageType: PrintPageType.SINGLE_MIMIC,
                mimicName: mimicName,
                vessel: this.vessel,
                iAPIVesselId: this.vessel.iAPIVesselId,
                shipyardId: this.vessel.shipyardId,
                options: [],
                notMain: true,
            },
        });

        const shouldPrint = await lastValueFrom(dialogRef.afterClosed());
        if (!shouldPrint || shouldPrint.cancel) return;
        this.editorService.showLoadingSpinner(true);
        const { printType, version, iAPIShipyardlId } = shouldPrint;

        if (printType) {
            const mimicToPrint = _.cloneDeep(mimic);

            if (this.selectedMimic && mimicToPrint.id == this.selectedMimic.id) {
                const svgContent = this.getSvgToPrint();
                mimicToPrint.svgcontent = svgContent;
            }
            if (printType == PrintType.NO_UPDATES) {
                this.printMimicsService.addMimicToPrint(mimicToPrint, this.images);
            } else if (printType == PrintType.UPDATES) {
                if (!iAPIShipyardlId || !version) return;
                const oldMimics = await this.pullMimics(iAPIShipyardlId, version);
                const oldMimic = oldMimics.find(mimic => mimic.id == mimicToPrint.id);
                const updatedSvg = this.printMimicsService.compareMimic(mimicToPrint, oldMimic);
                mimicToPrint.svgcontent = updatedSvg;
                this.printMimicsService.addMimicToPrint(mimicToPrint, this.images);
            }
            this.editorService.showLoadingSpinner(false);
            this.printMimicsService.printMimicsAsPdf(this.images, [mimicToPrint]);
        }
    }

    getSvgToPrint() {
        return this.editorMimicActionsService.serializeCurrentSvg();
    }

    async pullMimics(iAPIShipyardlId, version): Promise<Mimic[]> {
        const iAPIVesselId = this.vessel.iAPIVesselId;
        const oldMimics = await this.pullService.pullMimics(iAPIShipyardlId, iAPIVesselId, version);
        return oldMimics;
    }

    selectTab(tab: TAB) {
        this.selectedTab = tab;
        this.showOption = null;
    }

    async openComponentDialog(category: LibraryCategory) {
        this.showOption = undefined;
        this.showOptionsAll = false;
        this.showCategoryOption = false;
        this.onOpenComponentDialog.emit();

        if (this.selectedCategory?.id === category.id && this.showComponentDialog) {
            this.selectedCategory = undefined;
            this.selectedCategoryComponentList = [];
            this.showComponentDialog = false;
            return;
        }

        this.selectedCategory = category;
        this.showComponentDialog = true;
        this.selectedCategoryComponentList = [];

        if (!category.id.startsWith('default-')) {
            try {
                this.selectedCategoryComponentList = await this.libraryComponentService.getByLibraryCategoryId(
                    category.id
                );
            } catch (error) {
                console.log(error);
            }
        }
    }

    closeComponentDialog() {
        this.selectedCategory = undefined;
        this.showComponentDialog = false;
        this.selectedCategoryComponentList = [];
    }

    deleteLibrary(library: Library) {
        this.onDeleteLibrary.emit(library);
    }

    deleteLibraryCategory() {
        this.onDeleteCategory.emit(this.selectedCategory);
    }

    deleteLibraryComponent(component: LibraryComponent) {
        this.onDeleteComponent.emit(component);
    }

    deleteMimicFolder(mimicFolder: MimicFolderViewData) {
        this.onDeleteMimicFolder.emit(mimicFolder);
    }

    openEditLibraryCategoryDialog() {
        this.dialog.open(LibraryCategoryDialogComponent, {
            data: {
                libraryList: this.libraryList,
                vessel: this.vessel,
                library: undefined,
                selectedLibraryCategory: this.selectedCategory,
            },
        });
    }

    async duplicateLibraryCategory() {
        try {
            const existingNames =
                this.libraryList
                    .find(lib => lib.id === this.selectedCategory.libraryId)
                    ?.libraryCategories?.map(category => category.name) ?? [];

            const dialogRef = this.dialog.open(DuplicateMimicDialogComponent, {
                data: {
                    title: 'mimic.duplicate.duplicate-mimic-library-category.title',
                    initialName: this.selectedCategory.name,
                    existingNames: existingNames,
                    type: DuplicateItemType.CATEGORY,
                    submitButtonLabel: ButtonLabel.DUPLICATE,
                    autoIncrementName: true,
                    showExtraOptions: false,
                },
            });

            const { name: newName } = await lastValueFrom(dialogRef.afterClosed());

            if (!newName) return;

            let components = this.selectedCategoryComponentList;
            if (!components || components.length === 0) {
                if (!this.selectedCategory.id.startsWith('default-')) {
                    components = await this.libraryComponentService.getByLibraryCategoryId(this.selectedCategory.id);
                }
            }

            const componentsWithIdMapping = Utils.mapItemsWithSvgIdMapping(
                components,
                'svg',
                this.canvasService.getCanvas()
            );

            const categoryData = {
                ...this.selectedCategory,
                name: newName,
                componentList: componentsWithIdMapping,
            };

            await this.libraryCategoryService.duplicateLibraryCategory(this.selectedCategory.libraryId, categoryData);
        } catch (error) {
            console.log(error);
        }
    }

    closeCategoryOption() {
        this.showCategoryOption = false;
    }

    openFolderExpanded(folderId: string) {
        this.onOpenFolderExpanded.emit(folderId);
    }

    openLibraryExpanded(libraryId: string) {
        this.onOpenLibraryExpanded.emit(libraryId);
    }

    private getAllMimics() {
        return (
            this.rootList?.flatMap(item =>
                item.type === RootItemType.mimic ? [item.mimic] : (item.folder?.mimicList ?? [])
            ) ?? []
        );
    }
}
